import React, { useEffect, useRef, useState } from 'react';
import Credits, { formatBalance } from '../../services/credits';
import Field from '../atoms/Field/Field';
import '../../pages/MarketplacePage/MarketplaceDetailModal.css';
import './CouponModal.css';

/**
 * CouponModal — promo code redemption dialog.
 *
 * Reuses the shared `mkt-modal-*` shell so it visually sits next to
 * AddCreditsModal and the marketplace Deploy modal. Single input + Redeem
 * button. Two distinct UI states:
 *
 *   FORM    : code input + redeem button
 *   SUCCESS : "+$X credits added · new balance $Y" + Close
 *
 * Backend error reasons (see handlers/coupons.js) are mapped to specific
 * inline messages here. Anything unrecognized falls back to the generic
 * message from err.message — useful when the API surface gains new
 * reasons before the frontend's been updated.
 */

const REASON_COPY = {
  invalid_format:    'Enter a valid coupon code.',
  not_found:         "We don't recognize that code.",
  inactive:          'This code is no longer active.',
  expired:           'This code has expired.',
  sold_out:          'This code has been fully redeemed.',
  already_redeemed:  "You've already used this code.",
  malformed_meta:    'This code is misconfigured. Contact support.',
  grant_failed:      'Something went wrong applying the credit. Contact support.',
};

const CouponModal = ({ isOpen, onClose }) => {
  const [code, setCode] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [result, setResult] = useState(null);
  const inputRef = useRef(null);

  useEffect(() => {
    if (!isOpen) return;
    setCode('');
    setSubmitting(false);
    setError('');
    setResult(null);
    // Defer focus until the modal node has rendered.
    setTimeout(() => inputRef.current?.focus(), 0);
  }, [isOpen]);

  if (!isOpen) return null;

  const handleSubmit = async (e) => {
    if (e) e.preventDefault();
    if (submitting) return;
    setError('');
    setSubmitting(true);
    try {
      const res = await Credits.redeemCoupon(code);
      setResult(res);
    } catch (err) {
      const reason = err?.reason || err?.body?.reason || null;
      const msg = (reason && REASON_COPY[reason]) || err?.message || 'Could not redeem coupon';
      setError(msg);
    } finally {
      setSubmitting(false);
    }
  };

  const handleOverlayClick = () => {
    if (submitting) return;
    onClose();
  };

  return (
    <div className="mkt-modal-overlay" onClick={handleOverlayClick} role="presentation">
      <div
        className="mkt-modal coupon-modal"
        role="dialog"
        aria-label="Redeem coupon"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          type="button"
          className="mkt-modal-close"
          onClick={onClose}
          aria-label="Close"
          disabled={submitting}
        >×</button>

        <div className="mkt-modal-body">
          <header className="mkt-modal-header coupon-modal-header">
            <h2 className="mkt-modal-title">{result ? 'Coupon applied' : 'Redeem coupon'}</h2>
          </header>

          {!result ? (
            <form onSubmit={handleSubmit} noValidate>
              <label htmlFor="coupon-code-input" className="coupon-modal-label">
                Coupon code
              </label>
              <Field
                id="coupon-code-input"
                ref={inputRef}
                type="text"
                className="coupon-modal-input"
                placeholder="e.g. LAUNCH10"
                value={code}
                onChange={(e) => { setCode(e.target.value); if (error) setError(''); }}
                autoComplete="off"
                autoCapitalize="characters"
                spellCheck={false}
                maxLength={32}
                disabled={submitting}
              />

              {error ? (
                <div className="coupon-modal-error" role="alert">{error}</div>
              ) : (
                <p className="coupon-modal-help">
                  Enter the code exactly as you received it. Case and spaces don't matter.
                </p>
              )}

              <div className="coupon-modal-actions">
                <button
                  type="submit"
                  className="mkt-modal-deploy mkt-modal-deploy--full"
                  disabled={!code.trim() || submitting}
                >
                  {submitting ? 'Redeeming…' : 'Redeem'}
                </button>
              </div>
            </form>
          ) : (
            <div className="coupon-modal-success">
              <div className="coupon-modal-success-amount">
                +{formatBalance(Number(result.amountMilliCentsApplied) || 0)}
              </div>
              <div className="coupon-modal-success-desc">
                {result.description || `Code ${result.code}`}
              </div>
              {result.newBalanceMilliCents != null && (
                <div className="coupon-modal-success-balance">
                  New balance: <strong>{formatBalance(Number(result.newBalanceMilliCents) || 0)}</strong>
                </div>
              )}
              <div className="coupon-modal-actions">
                <button type="button" className="mkt-modal-deploy mkt-modal-deploy--full" onClick={onClose}>
                  Done
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default CouponModal;
